import { type FieldInputProps } from 'formik';
import { type ReactNode } from 'react';

import { FormikField } from '@air/formik-field';
import { Input } from '@air/primitive-input';

import { DIMENSION_BAR_INPUT } from '~/constants/testIDs';
import { type DimensionBarFormData, type DimensionBarProps, type DimensionMeasurement } from '../DimensionBar';

type DimensionBarInputWrapperProps = {
  children: ReactNode;
  dimensionMeasurement: DimensionMeasurement;
  onFocus: (isFocused: boolean) => void;
} & Pick<DimensionBarProps, 'dimensionUnits'>;

const DimensionBarInputWrapper = ({
  children,
  dimensionMeasurement,
  dimensionUnits,
  onFocus,
}: DimensionBarInputWrapperProps) => {
  return (
    <div className="flex items-center" onFocus={() => onFocus(true)} onBlur={() => onFocus(false)}>
      <p className="font-bold text-12 uppercase">{dimensionMeasurement}</p>
      {children}
      <p className="text-16 text-grey-11">{dimensionUnits}</p>
    </div>
  );
};

type DimensionBarInputProps = {
  ['data-testid']?: string;
  onChange: FieldInputProps<DimensionBarFormData['height'] | DimensionBarFormData['width']>['onChange'];
  onFocus: (isFocused: boolean) => void;
} & Pick<DimensionBarInputWrapperProps, 'dimensionMeasurement' | 'dimensionUnits'> &
  Pick<DimensionBarProps, 'disabled'>;

export const DimensionBarInput = ({
  dimensionMeasurement,
  dimensionUnits,
  disabled,
  onChange,
  onFocus,
  ['data-testid']: testId,
}: DimensionBarInputProps) => {
  return (
    <DimensionBarInputWrapper
      dimensionUnits={dimensionUnits}
      dimensionMeasurement={dimensionMeasurement}
      onFocus={onFocus}
    >
      <FormikField
        id={dimensionMeasurement}
        className="mx-1.5 w-16"
        isLabelHidden
        label={dimensionMeasurement}
        name={dimensionMeasurement}
        isErrorHidden
        onChange={onChange}
        component={
          <Input
            size="large"
            type="number"
            className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            data-testid={testId || DIMENSION_BAR_INPUT}
            disabled={disabled}
          />
        }
      />
    </DimensionBarInputWrapper>
  );
};
